import React, { useEffect, useState } from 'react';
import { CustomResponsiveTable } from '../../components/common/SimpleResponsiveTable';

import { Button, Empty, message, notification, Tag, Tooltip } from 'antd';
import { IParts, OrderLineItemProps, QuoteLineItemProps } from '../../types/types';
import QuoteStore from '../../stores/QuoteStore';
import { ErrorRender, Price } from '@partsbadger/library';
import { CheckOutlined, EditOutlined, ShoppingCartOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import AddToGroup from './AddToGroup';
import PartStore from 'stores/PartStore';

interface IPartHistory {
    master_product_id: number;
    part_data: IParts | undefined;
    goCheckoutPage: (
        order_items: {
            quantity: number;
            name: string;
            quote_has_product?: number;
            product_description: string;
            unit_price: string;
        }[]
    ) => void;
    editPart: (record: QuoteLineItemProps) => void;
}

interface IParams {
    master_product: number;
}

export default function QuoteHistory({ master_product_id, part_data, goCheckoutPage, editPart }: IPartHistory) {
    const [data, setData] = useState<OrderLineItemProps[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddToGroupModal, setShowAddToGroupModal] = useState<boolean>(false);

    const history = useHistory();

    async function fetch() {
        setLoading(true);

        const params: IParams = {
            master_product: master_product_id,
        };

        try {
            const response = await QuoteStore.getQuoteLineItemsByMasterId(params);
            setData(response.results);
            setLoading(false);
        } catch (err: any) {
            notification.error({
                duration: 10,
                message: 'Error',
                description: <ErrorRender error={err} />,
                placement: 'bottomRight',
            });
            setLoading(false);
        }
    }

    useEffect(() => {
        fetch();
    }, []);

    const emptyTable = () => {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span style={{ color: '#000' }}>This part doesn't have quotes.</span>}
            />
        );
    };

    return (
        <>
            <CustomResponsiveTable
                columns={[
                    {
                        title: 'Quote',
                        dataIndex: 'quote_related',
                        width: 100,
                        render: (_, record) => {
                            return (
                                <div
                                    className="cursor-pointer"
                                    onClick={() => {
                                        if (record.quote_related.completed && !record.quote_related.cancelled) {
                                            history.push(`/my-account/quotes/${record.quote_related.id}/`);
                                        } else {
                                            message.info(
                                                'This quote is not completed yet, after completion you will be able to access it',
                                                1
                                            );
                                        }
                                    }}
                                >
                                    <div>{record.quote_related.name}</div>
                                    {record.quote_related.cancelled ? (
                                        <Tag color="red">{record.quote_related.stage}</Tag>
                                    ) : record.quote_related.completed ? (
                                        <Tag color="green">Completed</Tag>
                                    ) : (
                                        <Tag color="orange">In Progress</Tag>
                                    )}
                                </div>
                            );
                        },
                    },
                    {
                        title: 'Information',
                        dataIndex: 'product.material_related.name',
                        width: 113,
                        render: (_: string, record: QuoteLineItemProps) => (
                            <>
                                <b>Material: </b> {record.product.material_related.name} <br />
                                <b>Finish: </b> {record.product.finish_related.name} <br />
                                <b>Coating: </b> {record.product.coating_related.name}
                            </>
                        ),
                    },
                    {
                        title: 'Date',
                        dataIndex: 'created_time',
                        width: 110,
                        render: (text, record) => {
                            return (
                                <div>
                                    <div>{dayjs(record.created_time).format('YYYY-MM-DD')}</div>
                                    <div className={'italic'}>By {record.contact_related?.fullname}</div>
                                </div>
                            );
                        },
                    },
                    {
                        title: 'Qty',
                        dataIndex: 'quantity',
                        width: 100,
                    },
                    {
                        title: 'Unit Price',
                        dataIndex: 'unit_price',
                        width: 100,
                        render: (unit_price: string, record) => {
                            if (record.quote_related.completed) {
                                return (
                                    <>
                                        <Price value={unit_price} />
                                    </>
                                );
                            }

                            return <div>Quoting</div>;
                        },
                    },
                    {
                        title: 'Total Price',
                        dataIndex: 'total_price',
                        width: 100,
                        render: (total_price: string, record) => {
                            if (record.quote_related.completed) {
                                return (
                                    <>
                                        <Price value={total_price} />
                                    </>
                                );
                            }

                            return <div>Quoting</div>;
                        },
                    },
                    {
                        title: 'Actions',
                        dataIndex: 'actions',
                        width: 150,
                        align: 'center',
                        render: (_: string, record) => (
                            <div className={'flex flex-col'}>
                                <Tooltip
                                    title={
                                        !record?.quote_related?.completed
                                            ? 'Apologies, but it seems that there are no completed quotes available for this part, which is necessary to proceed with the order.'
                                            : false
                                    }
                                >
                                    {/*<Button*/}
                                    {/*    type="link"*/}
                                    {/*    onClick={() => {*/}
                                    {/*        editPart(record);*/}
                                    {/*    }}*/}
                                    {/*    disabled={!record?.quote_related?.completed || record.quote_related.cancelled}*/}
                                    {/*    icon={<EditOutlined />}*/}
                                    {/*    className="flex items-center m-auto"*/}
                                    {/*>*/}
                                    {/*    Edit Quote*/}
                                    {/*</Button>*/}

                                    <Button
                                        type="link"
                                        icon={<CheckOutlined />}
                                        disabled={!record.quote_related.completed || record.quote_related.cancelled}
                                        onClick={() => {
                                            goCheckoutPage([
                                                {
                                                    quantity: record.quantity,
                                                    name: record.name,
                                                    quote_has_product: record.id,
                                                    product_description: '',
                                                    unit_price: record.unit_price,
                                                },
                                            ]);
                                        }}
                                        className="flex items-center m-auto"
                                    >
                                        Order Now
                                    </Button>

                                    <Button
                                        type="link"
                                        icon={<ShoppingCartOutlined />}
                                        disabled={!record.quote_related.completed || record.quote_related.cancelled}
                                        onClick={async () => {
                                            await QuoteStore.setSelectedLineItems(record);
                                            await notification.success({
                                                duration: 1,
                                                message: 'Part Added to the Cart',
                                                description:
                                                    'Your item has been added to the cart. You can proceed to checkout in My Cart.',
                                                placement: 'topRight',
                                            });
                                        }}
                                        className="flex items-center m-auto"
                                    >
                                        Add To Cart
                                    </Button>
                                    <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        disabled={!record.quote_related.completed || record.quote_related.cancelled}
                                        onClick={async () => {
                                            // await QuoteStore.setSelectedLineItems(record);
                                            PartStore.setSelectedlineItemsToGroup(record.id);
                                            setShowAddToGroupModal(true);
                                        }}
                                        className="flex items-center m-auto"
                                    >
                                        Add To Group
                                    </Button>
                                </Tooltip>
                            </div>
                        ),
                    },
                ]}
                loading={loading}
                data={data}
                pagination={false}
                emptyTable={emptyTable}
            />

            <AddToGroup
                showAddToGroup={showAddToGroupModal}
                onClose={() => {
                    setShowAddToGroupModal(false);
                }}
            />
        </>
    );
}
